import * as THREE from 'three';
import { Worker } from './worker';
import { addDeskStation } from './interior-scene';
import {
  DESK_POSITIONS, WHITEBOARD_POSITION,
  MGR_FRONT_CENTER, MGR_FACE_CAMERA_ROT,
  WORKER_DESK_OFFSET_Z,
} from '@/utils/constants';
import type { RoomState, WorkerState } from '@/types';
import type { TaskPlanItem } from '@/data/types';
import { extractDeskIndex } from '@/utils/worker-utils';

/**
 * Manages multiple workers: add/remove/layout, per-worker action commands.
 * Supports up to 4 workers (one per desk position).
 */
export class WorkerManager {
  private workers: Worker[] = [];
  private nextDeskIndex = 1; // 0 is already occupied by initial worker
  private scene: THREE.Scene;

  constructor(scene: THREE.Scene) {
    this.scene = scene;

    // Create the initial worker at desk 0
    const initialPos = DESK_POSITIONS[0].position.clone();
    initialPos.z += WORKER_DESK_OFFSET_Z; // In front of desk
    const worker0 = new Worker('worker-0', 0, initialPos);
    worker0.performAction('type'); // Start typing at their desk
    this.scene.add(worker0.group);
    this.workers.push(worker0);
  }

  /**
   * Add a new coworker. Returns the worker or null if room is full.
   */
  addWorker(): Worker | null {
    if (this.nextDeskIndex >= DESK_POSITIONS.length) return null;

    const deskIndex = this.nextDeskIndex;
    const id = `worker-${deskIndex}`;

    // Add desk station (desk, monitor, keyboard, light)
    addDeskStation(this.scene, deskIndex);

    // Create worker at desk position
    const startPos = DESK_POSITIONS[deskIndex].position.clone();
    startPos.z += WORKER_DESK_OFFSET_Z; // In front of desk
    const worker = new Worker(id, deskIndex, startPos);
    worker.performAction('type'); // Immediately start typing

    this.scene.add(worker.group);
    this.workers.push(worker);
    this.nextDeskIndex++;

    return worker;
  }

  /**
   * Command a specific worker to perform an action.
   */
  commandWorker(workerIndex: number, actionId: string): void {
    const worker = this.workers[workerIndex];
    if (!worker) return;
    worker.performAction(actionId);
  }

  /**
   * Update all workers.
   */
  update(delta: number): void {
    for (const worker of this.workers) {
      worker.update(delta);
    }
  }

  getWorkerCount(): number {
    return this.workers.length;
  }

  getWorker(index: number): Worker | undefined {
    return this.workers[index];
  }

  /**
   * Walk a worker to an arbitrary position (used by onboarding).
   */
  walkWorkerTo(
    workerIndex: number,
    target: THREE.Vector3,
    rotation: number,
    arrivalState?: WorkerState,
    onComplete?: () => void,
  ): Worker | undefined {
    const worker = this.workers[workerIndex];
    if (!worker) return undefined;
    worker.walkToPosition(target, rotation, arrivalState, onComplete);
    return worker;
  }

  /**
   * Add all remaining workers at once (for onboarding setup).
   */
  addAllWorkers(): void {
    while (this.nextDeskIndex < DESK_POSITIONS.length) {
      this.addWorker();
    }
  }

  /**
   * Get all workers (for onboarding effects).
   */
  getAllWorkers(): Worker[] {
    return [...this.workers];
  }

  /**
   * Toggle highlight glow on a worker (for hover feedback).
   */
  highlightWorker(deskIndex: number, on: boolean): void {
    const worker = this.workers.find(w => w.deskIndex === deskIndex);
    if (worker) worker.setHighlight(on);
  }

  /**
   * Set a worker's active indicator (green pulsing ring).
   * Shows which worker is currently executing a real AI task.
   */
  setWorkerActive(workerId: string, active: boolean): void {
    const worker = this.workers.find(w => w.id === workerId);
    if (!worker) {
      const idx = extractDeskIndex(workerId);
      if (idx !== null) {
        const w = this.workers.find(w2 => w2.deskIndex === idx);
        if (w) w.setActiveIndicator(active);
      }
      return;
    }
    worker.setActiveIndicator(active);
  }

  /**
   * Set a worker's thinking state (from SSE / chat events).
   */
  setWorkerThinking(workerId: string, thinking: boolean): void {
    const worker = this.workers.find(w => w.id === workerId);
    if (worker) {
      worker.setThinking(thinking);
    }
  }

  /**
   * Set a worker's talking state (mouth animation).
   */
  setWorkerTalking(workerId: string, talking: boolean): void {
    const worker = this.workers.find(w => w.id === workerId);
    if (!worker) {
      const idx = extractDeskIndex(workerId);
      if (idx !== null) {
        const w = this.workers.find(w2 => w2.deskIndex === idx);
        if (w) w.setTalking(talking);
      }
      return;
    }
    worker.setTalking(talking);
  }

  // ── Working Animation Loop Timers ──────────────────────────
  private workingAnimTimers = new Map<number, ReturnType<typeof setInterval>>();

  /**
   * Handle a task status update — animate the worker based on task type.
   * Called from interior scene wiring when WebSocket 'task-update' arrives.
   */
  handleTaskUpdate(workerId: string, status: string, taskType: string): void {
    const deskIndex = extractDeskIndex(workerId);
    if (deskIndex === null) return;
    const worker = this.workers.find(w => w.deskIndex === deskIndex);
    if (!worker) return;

    if (status === 'in_progress') {
      // Route animation by task type
      const actionId = this.taskTypeToAction(taskType);
      worker.performAction(actionId);
      // Start periodic working animation (desk → whiteboard → desk loop)
      this.startWorkingAnimation(deskIndex);
    } else if (status === 'completed' || status === 'failed') {
      this.stopWorkingAnimation(deskIndex);
      // Walk worker back to their desk (TYPING state — looks like working)
      this.walkWorkerBackToDesk(deskIndex);
    }
  }

  /**
   * Start a periodic working animation loop for a worker executing a task.
   * Worker types at desk, periodically visits whiteboard (every 15-20s),
   * thinks for 3s, then returns to desk. Gives constant visual feedback.
   */
  startWorkingAnimation(deskIndex: number): void {
    this.stopWorkingAnimation(deskIndex); // Clear any existing timer

    const loop = () => {
      const worker = this.workers.find(w => w.deskIndex === deskIndex);
      if (!worker || worker.isCurrentlyWalking) return;

      // Walk to whiteboard
      this.walkWorkerToWhiteboard(deskIndex, () => {
        // Think at whiteboard for 3s, then walk back
        setTimeout(() => {
          const w = this.workers.find(w2 => w2.deskIndex === deskIndex);
          if (!w) return;
          // Only walk back if we're still running this animation
          if (this.workingAnimTimers.has(deskIndex)) {
            this.walkWorkerBackToDesk(deskIndex);
          }
        }, 3000);
      });
    };

    // First visit after 15-20s
    const firstDelay = 15000 + Math.random() * 5000;
    const timer = setTimeout(() => {
      loop();
      // Subsequent visits every 15-20s
      const recurring = setInterval(() => {
        if (!this.workingAnimTimers.has(deskIndex)) {
          clearInterval(recurring);
          return;
        }
        loop();
      }, 15000 + Math.random() * 5000);
      this.workingAnimTimers.set(deskIndex, recurring);
    }, firstDelay) as unknown as ReturnType<typeof setInterval>;

    this.workingAnimTimers.set(deskIndex, timer);
  }

  /**
   * Stop the periodic working animation for a worker.
   */
  stopWorkingAnimation(deskIndex: number): void {
    const timer = this.workingAnimTimers.get(deskIndex);
    if (timer) {
      clearInterval(timer);
      this.workingAnimTimers.delete(deskIndex);
    }
  }

  /**
   * Map task_type to an action from the action registry.
   */
  private taskTypeToAction(taskType: string): string {
    switch (taskType) {
      case 'research':
      case 'analyze':
        return 'type';   // At desk, researching/analyzing
      case 'draft':
      case 'general':
        return 'type';   // At desk, writing
      case 'plan':
        return 'write';  // At whiteboard, planning
      default:
        return 'type';   // Default to desk work
    }
  }

  /**
   * Boss delegation animation — Alex (worker-0) walks to each assigned worker's
   * desk, pauses briefly (hand-off visual), then proceeds to the next one.
   * After visiting all workers, Alex returns to their own desk.
   *
   * @param assignedDeskIndices - Desk indices of workers being assigned tasks
   * @param onEachVisit - Called when boss arrives at each worker
   * @param onComplete - Called when boss returns to their desk
   */
  delegateToWorkers(
    assignedDeskIndices: number[],
    onEachVisit?: (deskIndex: number) => void,
    onComplete?: () => void,
    pauseMs = 800,
  ): void {
    const boss = this.workers[0];
    if (!boss) { onComplete?.(); return; }

    // Filter to valid, non-boss desk indices
    const validIndices = assignedDeskIndices.filter(
      i => i > 0 && i < DESK_POSITIONS.length && this.workers.find(w => w.deskIndex === i),
    );

    if (validIndices.length === 0) {
      onComplete?.();
      return;
    }

    // Chain sequential walks: boss → worker-1 desk → worker-2 desk → ... → back to own desk
    let step = 0;

    const visitNext = () => {
      if (step < validIndices.length) {
        const targetDeskIndex = validIndices[step];
        // Walk to a point near the target worker's desk (in front, offset slightly)
        const targetDesk = DESK_POSITIONS[targetDeskIndex].position.clone();
        // Stand beside the worker (offset toward center of room)
        const direction = targetDesk.x > 0 ? -0.8 : 0.8;
        const visitPos = new THREE.Vector3(
          targetDesk.x + direction,
          0,
          targetDesk.z + WORKER_DESK_OFFSET_Z, // In front of desk
        );
        // Face toward the worker
        const faceWorkerRot = Math.atan2(
          targetDesk.x - visitPos.x,
          targetDesk.z - visitPos.z,
        );

        boss.walkToPosition(visitPos, faceWorkerRot, 'IDLE', () => {
          // Boss arrived at worker's desk — trigger callback
          onEachVisit?.(targetDeskIndex);

          // Pause then move to next worker
          step++;
          setTimeout(visitNext, pauseMs);
        });
      } else {
        // All workers visited — boss walks back to their own desk
        const bossDesk = DESK_POSITIONS[0].position.clone();
        bossDesk.z += WORKER_DESK_OFFSET_Z;
        const bossDeskRot = DESK_POSITIONS[0].rotation + Math.PI;
        boss.walkToPosition(bossDesk, bossDeskRot, 'TYPING', () => {
          onComplete?.();
        });
      }
    };

    visitNext();
  }

  // ── Activity Masking Methods ──────────────────────────────────

  /**
   * Walk a worker to the whiteboard (WRITING state on arrival).
   * Used to mask AI latency — worker looks busy while AI processes.
   */
  walkWorkerToWhiteboard(workerIndex: number, onArrive?: () => void): void {
    const worker = this.workers[workerIndex];
    if (!worker) return;

    const whiteboardPos = new THREE.Vector3(
      WHITEBOARD_POSITION.x + 0.3,
      0,
      WHITEBOARD_POSITION.z + workerIndex * 0.6, // offset per worker
    );
    const whiteboardRot = -Math.PI / 2; // Face the whiteboard

    worker.walkToPosition(whiteboardPos, whiteboardRot, 'WRITING', () => {
      onArrive?.();
    });
  }

  /**
   * Walk a worker back to their desk (TYPING state on arrival).
   */
  walkWorkerBackToDesk(workerIndex: number, onArrive?: () => void): void {
    const worker = this.workers[workerIndex];
    if (!worker) return;

    const deskPos = DESK_POSITIONS[workerIndex].position.clone();
    deskPos.z += WORKER_DESK_OFFSET_Z;
    const deskRot = DESK_POSITIONS[workerIndex].rotation + Math.PI;

    worker.walkToPosition(deskPos, deskRot, 'TYPING', () => {
      onArrive?.();
    });
  }

  /**
   * Walk a worker to front-center (facing camera). Used for Alex's plan presentation.
   */
  walkWorkerToFrontCenter(workerIndex: number, onArrive?: () => void): void {
    const worker = this.workers[workerIndex];
    if (!worker) return;

    const target = MGR_FRONT_CENTER.clone();
    worker.walkToPosition(target, MGR_FACE_CAMERA_ROT, 'IDLE', () => {
      onArrive?.();
    });
  }

  /**
   * Trigger celebration animation on all workers.
   * After 2s, restores previous states and calls onComplete.
   */
  celebrateAll(onComplete?: () => void): void {
    for (const worker of this.workers) {
      worker.setCelebrating(true);
    }
    setTimeout(() => {
      for (const worker of this.workers) {
        worker.setCelebrating(false);
      }
      onComplete?.();
    }, 2000);
  }

  /**
   * Resolve worker role name → desk index. Returns -1 if not found.
   */
  resolveRoleToDeskIndex(role: string): number {
    const roleMap: Record<string, number> = {
      'Manager': 0,
      'Designer': 1,
      'Product Manager': 2,
      'Marketing': 3,
    };
    return roleMap[role] ?? -1;
  }

  /**
   * Serialize current room state for persistence/AI integration.
   */
  getState(): RoomState {
    return {
      workers: this.workers.map((w) => ({
        id: w.id,
        deskIndex: w.deskIndex,
        currentAction: w.getCurrentAction(),
      })),
      furnitureLayout: this.collectFurnitureLayout(),
    };
  }

  /**
   * Restore room state (future feature).
   */
  applyState(_state: RoomState): void {
    // TODO: Implement state restoration for AI integration
    console.log('applyState called — not yet implemented');
  }

  private collectFurnitureLayout(): RoomState['furnitureLayout'] {
    const items: RoomState['furnitureLayout'] = [];
    this.scene.traverse((obj) => {
      if (obj.userData.type && obj.userData.id && obj.userData.type !== 'worker') {
        items.push({
          id: obj.userData.id as string,
          type: obj.userData.type as string,
          position: {
            x: obj.position.x,
            y: obj.position.y,
            z: obj.position.z,
          },
        });
      }
    });
    return items;
  }
}
